import React from 'react';
import Avatar from '../../../components/common/Avatar';

/**
 * En leverantör som utfällbar rad. Används både på kundens Erbjudanden-sida
 * och i sektionen på MyProjects innan projektet startat.
 *
 * På Erbjudanden-sidan (showAction) ligger kontaktknappen direkt i raden så att
 * kunden slipper fälla ut för att komma vidare.
 */
const ErbjudandeRad = ({
  supplier,
  expanded,
  onToggle,
  onContact,
  onInfo,
  onToggleFavorite,
  isFavorite,
  requestedAt,
  showFavorite = false,
  showAction = false,
}) => {
  const kontaktKnappText = supplier.contactMethod === 'affiliate'
    ? 'Besök hemsida'
    : supplier.contactMethod === 'leadme'
      ? 'Kontakta'
      : 'Kontakta direkt';

  // Förfrågningar via Leadme sparas, så vi visar datum i stället för knappen igen
  const redanSkickad = supplier.contactMethod === 'leadme' && requestedAt;

  const skickatDatum = requestedAt
    ? new Date(requestedAt).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })
    : '';

  const kontaktKnapp = (extraKlasser = '') => (
    redanSkickad ? (
      <span className={`px-4 py-2 bg-gray-100 text-gray-400 font-medium rounded-lg cursor-default whitespace-nowrap ${extraKlasser}`}>
        Förfrågan skickad {skickatDatum}
      </span>
    ) : (
      <button
        onClick={() => onContact(supplier)}
        className={`px-4 py-2 bg-leadme-pink text-white font-medium rounded-lg hover:bg-leadme-pink-dark transition-colors whitespace-nowrap ${extraKlasser}`}
      >
        {kontaktKnappText}
      </button>
    )
  );

  return (
    <div>
      <div className="w-full px-4 sm:px-6 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors">
        <button
          onClick={onToggle}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
        >
          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-white border border-gray-100">
            <Avatar entity={supplier} size="sm" shape="rectangle" className="w-full h-full" />
          </div>
          <div className="min-w-0">
            <span className="block font-medium text-gray-900 text-sm sm:text-base truncate">
              {supplier.companyName}
            </span>
            {supplier.supplierType && (
              <span className="block text-xs text-gray-500 truncate">{supplier.supplierType}</span>
            )}
          </div>
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          {showFavorite && (
            <button
              type="button"
              aria-label={isFavorite ? 'Ta bort favorit' : 'Spara som favorit'}
              onClick={() => onToggleFavorite(supplier)}
              className={`p-1.5 rounded-full transition-colors ${
                isFavorite
                  ? 'text-leadme-pink hover:bg-pink-50'
                  : 'text-gray-300 hover:text-leadme-pink hover:bg-pink-50'
              }`}
            >
              <svg className="w-5 h-5" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          )}

          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
            {supplier.discountRate}% rabatt
          </span>

          {/* Kontaktknappen direkt i raden — döljs på små skärmar där den inte får plats */}
          {showAction && kontaktKnapp('hidden md:inline-flex text-xs')}

          <button
            type="button"
            onClick={onToggle}
            aria-label={expanded ? 'Dölj information' : 'Visa information'}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 sm:px-6 pb-4 space-y-3">
          <p className="text-sm text-gray-600">
            {supplier.description || 'Leverantören erbjuder kvalitetsprodukter med specialrabatt för våra kunder.'}
          </p>
          {supplier.discountDescription && (
            <p className="text-xs text-gray-500">{supplier.discountDescription}</p>
          )}
          {supplier.discounts && supplier.discounts.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 mb-1 font-medium">Specialrabatter:</div>
              <div className="flex flex-wrap gap-1">
                {supplier.discounts.map((discount, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs">
                    <span className="font-medium">{discount.rate}%</span>
                    <span className="ml-1">{discount.category}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-3 pt-1">
            {/* Redan synlig i raden på desktop när showAction är på */}
            {kontaktKnapp(showAction ? 'md:hidden text-sm' : 'text-sm')}
            <button
              onClick={() => onInfo(supplier)}
              className="px-4 py-2 border border-leadme-pink text-leadme-pink text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Mer information
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErbjudandeRad;
